'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

import CompleteRegistrationModal, {
  MODAL_ID as COMPLETE_REGISTRATION_MODAL_ID,
} from 'lib/components/CompleteRegistrationModal/CompleteRegistrationModal'
import { MOBILE_VERIFY_MODAL_ID } from 'lib/components/MobileVerification/constants'
import { useModal } from 'lib/components/Modal/Modal'
import { getOrderIdFromUrl } from 'lib/utils/orderUtils'

import { useGetPartialUserApi } from '@/api/useGetPartialUserApi'
import { useGuestSessionBootstrap } from '@/hooks/useGuestSessionBootstrap'
import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'

import OrderTrackingClient from './OrderTrackingClient'

const OrderTrackingPageClient: React.FC = () => {
  const { openDialog, hideDialog } = useModal()

  const searchParams = useSearchParams()
  const router = useRouter()
  const [orderId, setOrderId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)

  const hasHydrated = useSessionStore((state) => state.hasHydrated)
  const authToken = useSessionStore((store) => store.authToken)

  // `id` is what the force-registration email link and Rails' orders#show
  // bounce use; `_id` is what labels#print_all emits directly. Accept both —
  // otherwise the print-labels path arrives with an id we ignore, and the
  // registration modal never opens.
  const userId = useMemo(() => searchParams.get('id') ?? searchParams.get('_id'), [searchParams])
  const orderIdParam = useMemo(() => searchParams.get('order_id'), [searchParams])

  const { data: partialUser } = useGetPartialUserApi(userId)

  // Guests (guest_user_type set) must never be forced into registration —
  // their label-access path is the email/mobile-verify flow inside
  // OrderTracking. The registration modal is only for legacy temp-pass users
  // created on someone's behalf (guest_user_type null).
  const isGuestPartialUser = Boolean(partialUser?.guestUserType)

  // Port the guest's session so verification success persists to the backend
  // (via the auth token). Without a session, the verify modal only flips the
  // client-side flag — Rails print_all still sees an unverified user and
  // bounces right back to this page in a loop.
  useGuestSessionBootstrap(isGuestPartialUser ? userId : null)

  useEffect(() => {
    if (!authToken && hasHydrated && partialUser && !isGuestPartialUser) {
      openDialog(COMPLETE_REGISTRATION_MODAL_ID)
    }
  }, [authToken, hasHydrated, isGuestPartialUser, openDialog, partialUser])

  const onCompleteRegistrationModalSubmit = (data: Record<string, unknown>): void => {
    hideDialog(COMPLETE_REGISTRATION_MODAL_ID)

    // Thread the phone through dialogArgs, not through a local prop on our
    // own MobileVerifyModal instance — the modal is mounted globally by
    // ClientPortal, and mounting a second copy here caused two verify
    // modals to stack (the local one had no phone, ClientPortal's used
    // its own state), sending competing OTPs and invalidating each other.
    //
    // Also carry `?id=<user_id>` as guestUserId so the OTP-confirm
    // mutation has a target — the modal's own URL-param lookup only
    // checks `?guest_id=`, not this flow's `?id=` param, and the user
    // isn't signed in yet so there's no current_user either.
    openDialog(MOBILE_VERIFY_MODAL_ID, {
      phoneNumber: (data.user as User)?.phoneNumber ?? '',
      guestUserId: userId ?? undefined,
    })
  }

  useEffect(() => {
    const extractedOrderId = getOrderIdFromUrl()
    // based on timing we want make sure we only redirect if neither param is present
    const finalOrderId = orderIdParam || extractedOrderId
    if (finalOrderId) {
      setOrderId(finalOrderId)
      setIsLoading(false)
    } else {
      // No order ID found, redirect to main tracking page
      router.push('/track-orders')
    }
  }, [orderIdParam, router])

  // Don't render the client component until we have the orderId
  if (isLoading || !orderId) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <>
      <div className="flex w-full items-center">
        <OrderTrackingClient orderId={orderId} />
      </div>

      <CompleteRegistrationModal
        brand={brand.key}
        onSubmit={onCompleteRegistrationModalSubmit}
        defaultValues={partialUser}
        userId={userId}
        orderId={orderId}
        partialUser
      />
      {/* MobileVerifyModal is mounted globally by ClientPortal — do NOT
          mount another one here. Two instances with the same modal id
          both render on `openDialog`, producing the "two stacked verify
          modals" bug (one with the phone number, one without, both
          sending competing OTPs). */}
    </>
  )
}

export default OrderTrackingPageClient
